// Sticky Features Section JavaScript

const STICKY_SECTION_SELECTOR = '.sticky-features-section';
const stickyInstances = new WeakMap();

if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
  console.warn('GSAP ScrollTrigger not available for sticky features.');
} else {
  gsap.registerPlugin(ScrollTrigger);

  ready(() => initStickySections(document));

  document.addEventListener('shopify:section:load', (event) => {
    initStickySections(event.target);
    ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyStickySection(event.target);
    ScrollTrigger.refresh();
  });
}

function initStickySections(scope) {
  const sections = [];

  if (scope.matches && scope.matches(STICKY_SECTION_SELECTOR)) {
    sections.push(scope);
  }

  if (scope.querySelectorAll) {
    sections.push(...scope.querySelectorAll(STICKY_SECTION_SELECTOR));
  }

  sections.forEach((section) => {
    destroyStickySection(section); // ensure clean slate before init
    const instance = createStickyInstance(section);
    if (instance) {
      stickyInstances.set(section, instance);
    }
  });
}

function destroyStickySection(section) {
  const instance = stickyInstances.get(section);
  if (instance && typeof instance.destroy === 'function') {
    instance.destroy();
  }
  stickyInstances.delete(section);
}

function createStickyInstance(sectionRoot) {
  const wrapper = sectionRoot.querySelector('.sticky-features-wrapper');
  const introWrapper = sectionRoot.querySelector('.intro-wrapper');
  const tabsHeightEl = sectionRoot.querySelector('.tabs_height');
  const tabsComponent = sectionRoot.querySelector('.tabs_component');
  const contentPanels = Array.from(sectionRoot.querySelectorAll('.tabs_let-content'));
  const videoPanels = Array.from(sectionRoot.querySelectorAll('.tabs_video'));

  if (!wrapper || !introWrapper || !tabsHeightEl || !tabsComponent) {
    return null;
  }

  if (!contentPanels.length || contentPanels.length !== videoPanels.length) {
    return null;
  }

  const featureCount = contentPanels.length;
  const featureDuration = Math.max(0, parseNumber(wrapper.dataset.featureDuration, 550));
  const triggers = [];

  const counterEl = sectionRoot.querySelector('[data-sf-current]');

  const setActive = (index) => {
    contentPanels.forEach((panel, idx) => {
      const isActive = idx === index;
      panel.classList.toggle('is-1', isActive);
      if (videoPanels[idx]) {
        videoPanels[idx].classList.toggle('is-1', isActive);
      }
    });
    if (counterEl) {
      counterEl.textContent = String(index + 1).padStart(2, '0');
    }
  };

  setActive(0);

  const pinTargetId = wrapper.dataset.pinTarget;
  const pinElement = pinTargetId ? document.getElementById(pinTargetId) : introWrapper.querySelector('.text-align-center');

  if (pinElement) {
    triggers.push(
      ScrollTrigger.create({
        trigger: introWrapper,
        start: 'top top',
        end: 'bottom top',
        pin: pinElement,
        pinSpacing: false,
        invalidateOnRefresh: true
      })
    );
  }

  const getStickyHeight = () => tabsComponent.offsetHeight || window.innerHeight || 1;
  const getTotalHeight = () => {
    const fallbackTotal = featureCount * (getStickyHeight() + featureDuration);
    return tabsHeightEl.offsetHeight || fallbackTotal;
  };

  const getScrollDistance = () => {
    const scrollable = getTotalHeight() - getStickyHeight();
    return Math.max(scrollable, 1);
  };

  triggers.push(
    ScrollTrigger.create({
      trigger: tabsHeightEl,
      start: 'top top',
      end: () => `+=${getScrollDistance()}`,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        const progress = clamp(self.progress, 0, 0.999999);
        // Continuous scroll feedback: the CSS progress rail reads this var.
        wrapper.style.setProperty('--sf-progress', progress.toFixed(4));
        const index = Math.min(featureCount - 1, Math.floor(progress * featureCount));
        setActive(index);
      },
      onLeave: () => setActive(featureCount - 1),
      onEnterBack: () => setActive(0),
      onRefresh: () => setActive(0)
    })
  );

  return {
    destroy() {
      triggers.forEach((trigger) => trigger && trigger.kill());
      stickyInstances.delete(sectionRoot);
    }
  };
}

function parseNumber(value, fallback) {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function ready(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}
